'use client';

import React from 'react';
import { Button } from '@/components/Buttons/Button';

export interface DevFallbackToggleProps {
  forceAudioFallback: boolean;
  onToggle: () => void;
  forceIceTimeout: boolean;
  onToggleIceTimeout: () => void;
  showTokenUsage: boolean;
  onToggleTokenUsage: () => void;
}

/**
 * DEV-only controls. The caller gates visibility (localhost,
 * ?forceAudioFallback=1, ?forceIceTimeout=1, or ?showTokens=1).
 *
 * - "Force audio fallback": skips WebRTC entirely — instant fallback.
 * - "Simulate ICE timeout": substitutes bogus ICE servers so the peer goes
 *   through the full ICE gathering → checking → timeout path (simulates a
 *   real 701 STUN/TURN host-lookup failure).
 * - "Show tokens": toggles the DEV token readout. Reveal it in non-local
 *   environments with ?showTokens=1 (no fallback side effects).
 */
export const DevFallbackToggle: React.FC<DevFallbackToggleProps> = ({
  forceAudioFallback,
  onToggle,
  forceIceTimeout,
  onToggleIceTimeout,
  showTokenUsage,
  onToggleTokenUsage,
}) => (
  <div className="fixed bottom-2 left-2 z-40 flex flex-col gap-1">
    <Button
      className="bg-white/70 text-neutral-black ring-1 ring-neutral-gray-300"
      onClick={onToggle}
      size="small"
      variant={forceAudioFallback ? 'secondary' : 'tertiary'}
    >
      {`DEV · Force audio fallback: ${forceAudioFallback ? 'ON' : 'OFF'}`}
    </Button>
    <Button
      className="bg-white/70 text-neutral-black ring-1 ring-neutral-gray-300"
      onClick={onToggleIceTimeout}
      size="small"
      variant={forceIceTimeout ? 'secondary' : 'tertiary'}
    >
      {`DEV · Simulate ICE timeout (701): ${forceIceTimeout ? 'ON' : 'OFF'}`}
    </Button>
    <Button
      className="bg-white/70 text-neutral-black ring-1 ring-neutral-gray-300"
      onClick={onToggleTokenUsage}
      size="small"
      variant={showTokenUsage ? 'secondary' : 'tertiary'}
    >
      {`DEV · Show tokens: ${showTokenUsage ? 'ON' : 'OFF'}`}
    </Button>
  </div>
);
